import { MobXProviderContext } from 'mobx-react';
import { ConversationModel } from 'Models/ConversationModel';
import { useUIStore } from 'Modules/ui/index.store';
import React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { isNullOrUndefined } from 'Utils/typeGuards';

/**
 * Hook to handle opening chat details sidebar for a conversation.
 * Determines the type of conversation and opens the appropriate sidebar/details.
 *
 * @param conversation The conversation model instance
 * @returns A callback to open the chat details sidebar
 */
export const useOpenChatDetails = (conversation: ConversationModel) => {
  const { conversation: conversationStore, person: personStore } =
    React.useContext<RootStoreProps>(MobXProviderContext);

  const openChatDetails = React.useCallback(() => {
    if (conversation && ['Channel', 'Group'].includes(conversation.grouping)) {
      conversationStore.setChannelInfoDetails(conversation?.id, 'group-info');
      useUIStore
        .getState()
        .setEntitySidePanelInfo({ groupConversationId: conversation?.id });
    } else if (conversation && conversation.grouping === 'OneOnOne') {
      const otherConversationParticipants = conversation.participants.filter(
        (participant) => participant.personId !== personStore.loggedInPersonId
      );

      const mySelf = conversation.participants.find(
        (participant) => participant.personId === personStore.loggedInPersonId
      );

      if (
        !isNullOrUndefined(otherConversationParticipants) &&
        otherConversationParticipants.length === 1
      ) {
        const otherPersonId = otherConversationParticipants[0].personId ?? 0;

        if (otherPersonId !== 0) {
          useUIStore.getState().setEntitySidePanelInfo({
            entityId: otherPersonId.toString(),
            entityType: 'person',
          });
          conversationStore.setChannelInfoDetails(conversation?.id, 'new');
        } else {
          const otherPersonPhone =
            otherConversationParticipants[0].phone || null;

          const extrContact =
            otherPersonPhone &&
            personStore.getExtrContactByPhoneNumber(otherPersonPhone);
          if (extrContact) {
            useUIStore.getState().setEntitySidePanelInfo({
              entityId: otherPersonPhone,
              entityType: 'extrContact',
            });
            conversationStore.setChannelInfoDetails('0', 'new');
          } else {
            useUIStore.getState().setEntitySidePanelInfo({
              entityId: otherPersonPhone,
              entityType: 'unknownContact',
            });
            conversationStore.setChannelInfoDetails('0', 'new');
          }
        }
      } else {
        const mySelfPersonId = mySelf.personId ?? 0;

        useUIStore.getState().setEntitySidePanelInfo({
          entityId: mySelfPersonId.toString(),
          entityType: 'person',
        });
        conversationStore.setChannelInfoDetails('0', 'new');
      }
    }
  }, [conversation, conversationStore, personStore]);

  return openChatDetails;
};
